import React from "react";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, size } from "lodash";
import { Card } from "react-bootstrap";
import { FiUpload, FiAlertTriangle } from "react-icons/fi";
import {
  inviteStatusToEnglish,
  inviteStatusToType,
} from "statics/statusToEnglishMapping";

import moment from "moment";
import Button from "components/Button";
import StatusBox from "components/StatusBox";
import SimpleModal from "components/SimpleModal";
import AddEmployeeUploadFormToggle from "./AddEmployeeUploadFormToggle";
import Errors, { type FailedJob } from "./Errors";
import IconTable, { type IconTableColumn } from "components/IconTable";
import type { RootState } from "types/state";

import "./EmployeeInvitationsTable.scss";

interface UploadJobError {
  type?: string;
  format?: string;
  // (sic) — original PropTypes key is misspelled "resaon"; preserved verbatim
  resaon?: string;
}

interface OwnProps {
  isIconAdmin?: boolean;
  companyId?: string;
  errors?: UploadJobError[];
}

const mapStateToProps = (state: RootState) => {
  return {
    employeeUploadJobs: get(state, "employer.employeeUploadJobs", []) as Array<
      Record<string, unknown>
    >,
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  // read but never written after init
  initialFetching?: boolean;
  showForm: boolean;
  showErrorsModal: boolean;
  failedJobData: unknown;
}

export class EmployeeUploadJobTable extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      showForm: false,
      showErrorsModal: false,
      failedJobData: null,
    };
  }

  _buildColumns = () => {
    const commonDateFormat = "MMM Do YYYY";
    const columns: Array<IconTableColumn<Record<string, unknown>>> = [
      {
        label: "Group Name",
        key: "group.name",
      },

      {
        label: "File Name",
        key: "filename",
      },
      {
        label: "Processing Date",
        customComponent: (props) => (
          <>
            {moment(
              (props as { createdAt?: moment.MomentInput }).createdAt
            ).format(commonDateFormat)}
          </>
        ),
      },
      {
        label: "Status",
        customComponent: (props) => {
          const status = (
            props as { status?: keyof typeof inviteStatusToEnglish }
          ).status;
          return (
            <StatusBox
              status={
                inviteStatusToEnglish[
                  status as keyof typeof inviteStatusToEnglish
                ]
              }
              type={
                inviteStatusToType[status as keyof typeof inviteStatusToType]
              }
            />
          );
        },
      },
      {
        label: "Errors",
        customComponent: (props) => {
          const errors = (props as { errors?: object | null }).errors;
          const errorSize = size(errors);
          const hideErrors =
            (props as { status?: unknown }).status === "PROCESSING" ||
            errorSize === 0;

          const viewErrors = `View Errors ${errorSize}`;

          return (
            <div className="errors">
              {!hideErrors && (
                <Button
                  btnLabel={viewErrors}
                  name="action"
                  color="red"
                  size="sm"
                  icon={{
                    icon: <FiAlertTriangle size={14} color={"#fffff"} />,
                    position: "left",
                  }}
                  onClick={() =>
                    this.setState({
                      showErrorsModal: true,
                      failedJobData: props,
                    })
                  }
                />
              )}
            </div>
          );
        },
      },
    ];
    return columns;
  };

  _renderUploadsTable() {
    const columns = this._buildColumns();
    const processedFiles = size(this.props.employeeUploadJobs);

    return (
      <div className="group-table">
        {this.state.showErrorsModal && (
          <SimpleModal
            // @ts-expect-error — SimpleModal ignores className (inert prop); preserved from JS
            className="errors-modal"
            show={true}
            size={"lg"}
            onClose={() =>
              this.setState({ showErrorsModal: false, failedJobData: null })
            }
            title="Error Details"
            buttonText="close"
          >
            {/* failedJobData is set from a table row (unknown) just before this modal opens; cast to the Errors prop shape — preserved from the JS. */}
            <Errors failedJob={this.state.failedJobData as FailedJob} />
          </SimpleModal>
        )}
        <Card className="employee_-nvitations-table">
          <div className="widget-header">
            <span>
              <Card.Title>
                Files:
                <p className="invite-number">
                  Processed (<p className="number">{processedFiles}</p>)
                </p>
              </Card.Title>
            </span>
            <div className="action-btns">
              <span className="add-btn">
                <Button
                  icon={{
                    icon: <FiUpload size={14} color={"#fffff"} />,
                    position: "left",
                  }}
                  size="sm"
                  name="action"
                  onClick={() => this.setState({ showForm: true })}
                  btnLabel="Upload File"
                />
              </span>
            </div>
          </div>
          <IconTable columns={columns} data={this.props.employeeUploadJobs} />
        </Card>
      </div>
    );
  }

  render() {
    let content;

    if (this.state.showForm) {
      content = (
        <AddEmployeeUploadFormToggle
          onClose={() => this.setState({ showForm: false })}
          isIconAdmin={this.props.isIconAdmin}
          companyId={this.props.companyId}
          onUploadSuccess={() => {
            this.props.push("/dashboard/users/employees");
          }}
        />
      );
    } else {
      content = this._renderUploadsTable();
    }
    return (
      <div className="add-employee-form" style={{ marginBottom: 8 }}>
        {content}
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployeeUploadJobTable));
